import { isEmpty, isNil, throttle } from 'lodash-es';
import { rememberServerTime } from '@/common/utils/serverTime';
import CancellationError from '@/common/utils/errors/CancellationError';
import NetworkError from '@/common/utils/errors/NetworkError';
import ValidationError from '@/common/utils/errors/ValidationError';
import ServerError from '@/common/utils/errors/ServerError';
import extractFileInfo from '@/common/utils/fileInfoExtractor';
import {
  trackFileUploadStart,
  trackFileUploadComplete
} from '@/common/telemetry';
import bus from '@/common/utils/eventBus';
import InternalServerError from '@/common/utils/errors/InternalServerError';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import BaseHttpAdapter, { type HttpRequestOptions } from '@/common/utils/baseHttpAdapter';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosProgressEvent,
  type RawAxiosRequestHeaders
} from 'axios';
import isSelfService from '@/common/utils/isSelfService';
import { HTTP_STATUS_NOT_FOUND } from '@/common/constants/httpStatus.constants';

export default class AcronisHttpAdapter extends BaseHttpAdapter {
  private httpAdapter: AxiosInstance;

  constructor() {
    super();
    this.httpAdapter = axios.create({
      headers: this.defaultHeaders,
    });
  }

  public getInstance(): AxiosInstance {
    return this.httpAdapter;
  }

  public setInstance(instance: AxiosInstance): void {
    if (!instance) {
      throw new Error('Axios instance is required');
    }

    this.httpAdapter = instance;

    this.httpAdapter.defaults.headers.common = {
      ...this.defaultHeaders,
      ...instance.defaults.headers.common,
    };

    this.setRequestInterceptor();
    this.setResponseInterceptor();
  }

  public getHeaders(): RawAxiosRequestHeaders {
    return this.httpAdapter.defaults.headers.common;
  }

  private setRequestInterceptor() {
    this.httpAdapter.interceptors.request.use((config) => {
      config.headers['X-Session-Id'] = isSelfService() ? 1 : 0;

      const scopedToken = this.storeAdapter?.getScopedToken();

      if (!config.unscopedRequest && scopedToken) {
        config.headers[this.scopedTokenHeaderName] = scopedToken;
      }

      const requestControllerItem = this.createRequestController(config.abortController);
      config.signal = requestControllerItem.controller.signal;
      config.requestControllerItem = requestControllerItem;

      return config;
    });
  }

  private setResponseInterceptor() {
    this.httpAdapter.interceptors.response.use((response) => {
      rememberServerTime(response.headers?.date);
      const { requestControllerItem } = response.config;

      if (requestControllerItem) {
        this.removeController(requestControllerItem);
      }

      const { skipAuthGuard = false, forceAuthGuard = false } = response.config;

      this.authGuard(
        response,
        response.data,
        {
          skipAuthGuard,
          forceAuthGuard
        }
      );

      this.reconfigurationHook(response);

      if (
        response.status >= 200
        && response.status < 400
        && response.config.method === 'get'
      ) {
        bus.emit(response.data as CommonObject);
      }

      return response;
    }, (error) => {
      const { requestControllerItem } = error.config || {};

      if (requestControllerItem) {
        this.removeController(requestControllerItem);
      }

      return this.handleError(error);
    });
  }

  private handleError(error: AxiosError): void {
    const { response, name, code } = error;

    if (name === 'AbortError' || code === 'ERR_CANCELED') {
      throw new CancellationError(error);
    }

    if (!response) {
      throw new NetworkError(error);
    }

    const { status, data } = response;

    if (status === HTTP_STATUS_NOT_FOUND) {
      throw new NotFoundError(response, data as ErrorBody);
    }
    if (status >= 400 && status < 500) {
      throw new ValidationError(response, data as ErrorBody);
    }
    if (status >= 500) {
      throw new InternalServerError(response, data as ErrorBody);
    }

    throw new NetworkError(error);
  }

  get<T = any>(
    path: UriPath,
    params?: FetchRequestParams,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>> {
    return this.httpAdapter.get(
      path,
      {
        params,
        baseURL: options?.baseURL,
        headers: options?.headers,
        unscopedRequest: options?.unscopedRequest,
        skipAuthGuard: options?.skipAuthGuard,
        forceAuthGuard: options?.forceAuthGuard,
        abortController: options?.abortController,
      }
    );
  }

  post<T = any>(
    path: UriPath,
    body: FetchRequestBody = {},
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>> {
    return this.httpAdapter.post(
      path,
      body,
      {
        headers: options?.headers,
        unscopedRequest: options?.unscopedRequest,
        skipAuthGuard: options?.skipAuthGuard,
        forceAuthGuard: options?.forceAuthGuard,
        abortController: options?.abortController,
      }
    );
  }

  put<T = any>(
    path: UriPath,
    body: FetchRequestBody = {},
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>> {
    return this.httpAdapter.put(
      path,
      body,
      {
        headers: options?.headers,
        unscopedRequest: options?.unscopedRequest,
        skipAuthGuard: options?.skipAuthGuard,
        forceAuthGuard: options?.forceAuthGuard,
        abortController: options?.abortController,
      }
    );
  }

  patch<T = any>(
    path: UriPath,
    body: FetchRequestBody = {},
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>> {
    return this.httpAdapter.patch(
      path,
      body,
      {
        headers: options?.headers,
        unscopedRequest: options?.unscopedRequest,
        skipAuthGuard: options?.skipAuthGuard,
        forceAuthGuard: options?.forceAuthGuard,
        abortController: options?.abortController,
      }
    );
  }

  delete<T = any>(
    path: UriPath,
    body?: FetchRequestBody,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>> {
    const config = {
      headers: options?.headers,
      unscopedRequest: options?.unscopedRequest,
      skipAuthGuard: options?.skipAuthGuard,
      forceAuthGuard: options?.forceAuthGuard,
      abortController: options?.abortController,
    };

    if (!isNil(body) && !isEmpty(body)) {
      return this.httpAdapter.delete(path, { ...config, data: body });
    }

    return this.httpAdapter.delete(path, config);
  }

  async upload<T = any>(
    path: UriPath,
    body: Document | XMLHttpRequestBodyInit | null,
    options?: {
      method?: string;
      headers?: Record<string, string>;
      uploadTimeout?: number;
      fileName?: string;
    } & HttpRequestOptions,
    onProgress?: (progress: AxiosProgressEvent, xhr: XMLHttpRequest) => void
  ): Promise<T> {
    const { fileName, fileSize } = extractFileInfo(body, { fileName: options?.fileName });

    const uploadStartTime = trackFileUploadStart({
      fileName,
      fileSize,
      metadata: {
        upload_path: path,
        request_method: options?.method
      }
    });

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const abortController = options?.abortController || new AbortController();
    const uploadTimeout = options?.uploadTimeout || 60000;

    const throttledProgress = onProgress
      ? throttle((progressEvent: AxiosProgressEvent) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          trackFileUploadComplete({
            startTime: uploadStartTime,
            fileName,
            fileSize,
            success: false,
            errorMessage: 'Upload timeout',
            additionalProperties: {
              error_type: 'timeout',
              timeout_ms: uploadTimeout
            }
          });
          abortController.abort();
        }, uploadTimeout);
        onProgress(
          progressEvent,
          { abort: () => abortController.abort() } as XMLHttpRequest
        );
      }, 100)
      : undefined;

    try {
      const response = await this.httpAdapter.request({
        url: path,
        method: options?.method || 'POST',
        data: body,
        headers: {
          ...this.defaultHeaders,
          ...options?.headers,
        },
        onUploadProgress: throttledProgress,
        abortController,
        unscopedRequest: options?.unscopedRequest,
        skipAuthGuard: options?.skipAuthGuard,
        forceAuthGuard: options?.forceAuthGuard,
      });

      clearTimeout(timeoutId);

      trackFileUploadComplete({
        startTime: uploadStartTime,
        fileName,
        fileSize,
        success: true,
        additionalProperties: {
          status_code: response.status,
          response_size: JSON.stringify(response.data).length
        }
      });

      return {
        data: response.data,
        response
      } as T;
    } catch (err: unknown) {
      clearTimeout(timeoutId);

      const axiosError = err as {
        response?: { status: number; data: ErrorBody };
        code?: string;
        message?: string;
      };

      if (axiosError.code === 'ERR_CANCELED') {
        trackFileUploadComplete({
          startTime: uploadStartTime,
          fileName,
          fileSize,
          success: false,
          errorMessage: 'Upload cancelled',
          additionalProperties: { error_type: 'cancellation' }
        });
        throw new CancellationError(err);
      }

      if (!axiosError.response) {
        trackFileUploadComplete({
          startTime: uploadStartTime,
          fileName,
          fileSize,
          success: false,
          errorMessage: 'Network error',
          additionalProperties: { error_type: 'network_error' }
        });
        throw new NetworkError(err);
      }

      const { status, data } = axiosError.response;

      trackFileUploadComplete({
        startTime: uploadStartTime,
        fileName,
        fileSize,
        success: false,
        errorMessage: `HTTP ${status}`,
        additionalProperties: {
          status_code: status,
          error_category: status >= 500 ? '5xx' : '4xx'
        }
      });

      if (status >= 500) {
        throw new ServerError(axiosError.response as unknown as XMLHttpRequest, data);
      }

      throw new ValidationError(axiosError.response as unknown as XMLHttpRequest, data);
    }
  }
}
